import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { equipmentService } from '@/services/equipment.service';
import { receivingLotService } from '@/services/receiving-lot.service';
import { Button } from '@/components/ui/button';
import { useHasPermission } from '@/hooks/use-permissions';

/**
 * D-105 (Stage E, inward correction) — the reporting workflow lives here.
 *
 * The report is operationally about equipment RECEIVED into the workshop, so it
 * belongs beside the inward list rather than on the Equipment page. The scope is
 * an inward period (date range or month) plus an optional Receiving Lot / Batch.
 *
 * Nothing about the backend changed: this calls the same Stage E endpoints, the
 * same dataset service and the same `equipment:export` permission. Only the
 * entry point moved.
 *
 * There is deliberately no "Export All". The exact matching count is resolved by
 * the SERVER and shown before either format button becomes usable, and the
 * server independently re-resolves the dataset and enforces the cap when the
 * export actually runs — this count is informational.
 */

/** The cap the SERVER enforces (D-103/D-105). Mirrored only to explain the disabled state. */
const EXPORT_ROW_CAP = 5000;

const CONTROL_CLASS =
  'h-9 w-full min-w-0 rounded-lg border border-border bg-background px-3 text-sm text-foreground ' +
  'focus:outline-none focus:ring-2 focus:ring-ring';

const LABEL_CLASS = 'text-xs font-medium text-muted-foreground';

/** Month → the SAME [from, to] the date inputs produce; one semantic, never two. */
export function monthToRange(month: string): { from: string; to: string } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return null;
  const lastDay = new Date(Date.UTC(Number(m[1]), Number(m[2]), 0)).getUTCDate();
  return { from: `${m[1]}-${m[2]}-01`, to: `${m[1]}-${m[2]}-${String(lastDay).padStart(2, '0')}` };
}

function extractApiMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

export function InwardReportPanel() {
  const can = useHasPermission();
  const canExport = can('equipment:export');
  const canViewLots = can('receiving-lot:view');

  const [receivedFrom, setReceivedFrom] = useState('');
  const [receivedTo, setReceivedTo] = useState('');
  const [receivingLotId, setReceivingLotId] = useState('');
  const [exporting, setExporting] = useState<'pdf' | 'excel' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const { data: lots } = useQuery({
    queryKey: ['receiving-lots-inward-report'],
    queryFn: () => receivingLotService.list({ pageSize: 100, sortBy: 'lotNumber', sortDir: 'asc' }),
    enabled: canExport && canViewLots,
  });

  /** The scope, in the exact shape both the count query and the export send. */
  const scope = useMemo(
    () => ({
      receivedFrom: receivedFrom || undefined,
      receivedTo: receivedTo || undefined,
      receivingLotId: receivingLotId || undefined,
    }),
    [receivedFrom, receivedTo, receivingLotId],
  );

  // The SERVER resolves the count — the same filtered population the export will
  // produce. pageSize 1 because only `summary.total` is wanted, not the rows.
  const {
    data: preview,
    isFetching: counting,
    isError: countFailed,
  } = useQuery({
    queryKey: ['inward-report-count', receivedFrom, receivedTo, receivingLotId],
    queryFn: () => equipmentService.list({ ...scope, page: 1, pageSize: 1 }),
    enabled: canExport,
  });

  const matchingCount = preview ? (preview.summary?.total ?? preview.total) : null;
  const overCap = matchingCount !== null && matchingCount > EXPORT_ROW_CAP;
  const exportReady = matchingCount !== null && matchingCount > 0 && !overCap;

  const selectedMonth =
    receivedFrom.slice(0, 7) === receivedTo.slice(0, 7) ? receivedFrom.slice(0, 7) : '';

  const applyMonth = (month: string) => {
    const range = monthToRange(month);
    setReceivedFrom(range?.from ?? '');
    setReceivedTo(range?.to ?? '');
  };

  const clearScope = () => {
    setReceivedFrom('');
    setReceivedTo('');
    setReceivingLotId('');
    setExportError(null);
  };

  const runExport = async (format: 'pdf' | 'excel') => {
    if (exporting) return; // one export at a time; no duplicate clicks
    setExporting(format);
    setExportError(null);
    try {
      await equipmentService.exportReport(format, scope);
    } catch (error) {
      setExportError(extractApiMessage(error, 'Export failed. Please try again.'));
    } finally {
      setExporting(null);
    }
  };

  // Server-side `equipment:export` is the real control; this only hides a
  // control the operator could not use anyway.
  if (!canExport) return null;

  const scopeActive = Boolean(receivedFrom || receivedTo || receivingLotId);

  /** Why export is unavailable, when it is — a disabled button should explain itself. */
  const unavailableReason = counting
    ? 'Resolving the matching count…'
    : countFailed
      ? 'The matching count could not be resolved.'
      : matchingCount === 0
        ? 'No records match this scope.'
        : overCap
          ? `This scope exceeds the ${EXPORT_ROW_CAP.toLocaleString()}-record limit. Narrow the period or choose a lot.`
          : null;

  return (
    <section
      role="group"
      aria-label="Inward report"
      className="rounded-lg border border-border bg-card"
    >
      {/* ── header ───────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 px-4 pt-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">Inward Report</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Equipment received into the workshop. Choose a period and, optionally, a lot.
          </p>
        </div>
        {scopeActive ? (
          <Button variant="ghost" size="sm" onClick={clearScope}>
            Clear Scope
          </Button>
        ) : null}
      </div>

      {/* ── scope controls: 1 col on mobile, 2 on tablet, 4 on desktop ────── */}
      <div className="grid grid-cols-1 gap-3 px-4 py-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="min-w-0">
          <label htmlFor="inward-report-from" className={LABEL_CLASS}>
            Received From
          </label>
          <input
            id="inward-report-from"
            type="date"
            className={`mt-1 ${CONTROL_CLASS}`}
            value={receivedFrom}
            max={receivedTo || undefined}
            onChange={(e) => setReceivedFrom(e.target.value)}
          />
        </div>

        <div className="min-w-0">
          <label htmlFor="inward-report-to" className={LABEL_CLASS}>
            Received To
          </label>
          <input
            id="inward-report-to"
            type="date"
            className={`mt-1 ${CONTROL_CLASS}`}
            value={receivedTo}
            min={receivedFrom || undefined}
            onChange={(e) => setReceivedTo(e.target.value)}
          />
        </div>

        <div className="min-w-0">
          <label htmlFor="inward-report-month" className={LABEL_CLASS}>
            Month
          </label>
          <input
            id="inward-report-month"
            type="month"
            className={`mt-1 ${CONTROL_CLASS}`}
            value={selectedMonth}
            onChange={(e) => applyMonth(e.target.value)}
          />
        </div>

        {canViewLots ? (
          <div className="min-w-0">
            <label htmlFor="inward-report-lot" className={LABEL_CLASS}>
              Receiving Lot / Batch
            </label>
            <select
              id="inward-report-lot"
              className={`mt-1 ${CONTROL_CLASS}`}
              value={receivingLotId}
              onChange={(e) => setReceivingLotId(e.target.value)}
            >
              <option value="">All receiving lots</option>
              {(lots?.data ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.lotNumber}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      {/* ── footer: the count on the left, the two exports on the right ────
          Separated from the controls and given its own row so a long message
          can never push the buttons off the right edge, which is exactly what
          the previous single flex row did. */}
      <div className="flex flex-col gap-3 border-t border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">
            {counting || matchingCount === null
              ? 'Counting matching records…'
              : `${matchingCount} record${matchingCount === 1 ? '' : 's'} match current filters`}
          </p>
          {unavailableReason ? (
            <p
              className={`mt-0.5 text-xs ${overCap || countFailed ? 'text-destructive' : 'text-muted-foreground'}`}
            >
              {unavailableReason}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-shrink-0 sm:items-center">
          <Button
            size="sm"
            variant="secondary"
            className="w-full sm:w-auto"
            disabled={!exportReady || exporting !== null}
            aria-busy={exporting === 'pdf'}
            onClick={() => void runExport('pdf')}
          >
            {exporting === 'pdf' ? 'Preparing PDF…' : 'Export PDF'}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            className="w-full sm:w-auto"
            disabled={!exportReady || exporting !== null}
            aria-busy={exporting === 'excel'}
            onClick={() => void runExport('excel')}
          >
            {exporting === 'excel' ? 'Preparing Excel…' : 'Export Excel'}
          </Button>
        </div>
      </div>

      {exportError ? (
        <p className="border-t border-border px-4 py-2 text-xs text-destructive" role="alert">
          {exportError}
        </p>
      ) : null}
    </section>
  );
}
